import * as React from "react";
import { connect } from "react-redux";
import * as moment from "moment";
import { Card, Container, Button, Segment, Divider, Label, Grid } from "semantic-ui-react";

import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { CaseConversationTaskModel } from "@teamprevent/common/messaging/models";

import { ImmunisationTaskModel } from "../../task/model";
import { ApplicationState } from "../../applicationState";

import { ActionableTaskItem } from "./ActionableTaskItem";
import { ActionableTaskFilter } from "./ActionableTaskFilter";
import { ActionableTaskAssign } from "./ActionableTaskAssign";

interface OwnProps {
    onActionableTasksChanged?(tasks: (TaskModel | ImmunisationTaskModel | CaseConversationTaskModel)[], tasksCount: number): void;
}

import "../styles/actionable-task-list.scss";

interface StateProps {
    userId: string;
    dueDate: moment.Moment;
    initialTaskTypes?: string[];
    preselectAllAdminUsers: boolean;
}

const ActionableTaskListUnconnected: React.FunctionComponent<OwnProps & StateProps> = (props: OwnProps & StateProps) => {

    const [actionableTasks, setActionableTasks] = React.useState<(TaskModel | ImmunisationTaskModel | CaseConversationTaskModel)[]>([]);
    const [selectedTaskIds, setSelectedTaskIds] = React.useState<string[]>([]);
    const selectedTasks = React.useMemo(
        () => actionableTasks.filter(t => selectedTaskIds.includes(t.id)).map(t => ({ ...t, selected: true })),
        [actionableTasks, selectedTaskIds]);
    const [actionableTasksCount, setActionableTasksCount] = React.useState<number>(0);
    const [assignModalOpen, setAssignModalOpen] = React.useState<boolean>(false);

    const onSelectionChanged = React.useCallback((taskId: string, selected: boolean) => {
        setSelectedTaskIds(prevValue => selected ? [...prevValue, taskId] : prevValue.filter(id => id !== taskId));
    }, []);

    const onActionableTasksChanged = React.useCallback((tasks: (TaskModel | ImmunisationTaskModel | CaseConversationTaskModel)[], tasksCount: number) => {
        setActionableTasks(tasks);
        setActionableTasksCount(tasksCount);
    }, []);

    const onAssignClicked = React.useCallback(() => {
        setAssignModalOpen(true);
    }, []);

    const onAssignedOrCancelled = React.useCallback(() => {
        setAssignModalOpen(false);
    }, []);

    return (
        <>
            <Container>
                <ActionableTaskFilter
                    onActionableTasksChanged={onActionableTasksChanged}
                    userId={props.userId}
                    dueDate={props.dueDate}
                    initialTaskTypes={props.initialTaskTypes}
                    preselectAllAdminUsers={props.preselectAllAdminUsers}
                />
                <Divider hidden />
                <Segment attached>
                    <Grid>
                        <Grid.Row columns="2">
                            <Grid.Column floated="left">
                                <Button icon="add" disabled={selectedTasks.length === 0} content="Assign" onClick={onAssignClicked} />
                            </Grid.Column>
                            <Grid.Column floated="right">
                                <Label><strong>Total Tasks: {actionableTasksCount}</strong></Label>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
                <Divider hidden />
                <Card.Group>
                    {actionableTasks.map(task => (<ActionableTaskItem
                        key={task.id}
                        task={task}
                        userId={props.userId}
                        onSelectionChanged={onSelectionChanged}
                        selected={selectedTaskIds.indexOf(task.id) > -1}
                    />))}
                </Card.Group>
            </Container>
            <ActionableTaskAssign
                tasks={selectedTasks}
                open={assignModalOpen}
                onSaved={onAssignedOrCancelled}
                onCanceled={onAssignedOrCancelled}
            />
        </>
    );
};

function mapStateToProps(state: ApplicationState): StateProps {
    const { dueDate, userId } = state.router.query!;
    const dueDateAsMoment = dueDate && moment.utc(dueDate);
    const taskTypes = state.router.query!.taskTypes as unknown as string[];
    const preselectAllAdminUsersAsString = state.router.query!.preselectAllAdminUsers;
    const preselectAllAdminUsers = preselectAllAdminUsersAsString === "true" ? true : false;
    return {
        userId,
        dueDate: dueDateAsMoment || moment.utc().add(4, "months"),
        initialTaskTypes: taskTypes,
        preselectAllAdminUsers
    };
}

export const ActionableTaskList = connect(mapStateToProps, null)(ActionableTaskListUnconnected);
